import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaBitcoin, FaEthereum } from 'react-icons/fa';
import { SiTether } from 'react-icons/si';
import { CreditCard, Shield, Zap, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CRYPTO_OPTIONS = [
  { value: 'BTC', label: 'Bitcoin (BTC)', icon: FaBitcoin, color: '#F7931A' },
  { value: 'ETH', label: 'Ethereum (ETH)', icon: FaEthereum, color: '#627EEA' },
  { value: 'USDT', label: 'Tether (USDT)', icon: SiTether, color: '#26A17B' },
];

const QUICK_AMOUNTS = [5000, 10000, 50000, 100000, 500000];

export default function PaymentForm() {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    amount: '',
    crypto_type: 'BTC',
    wallet_address: '',
  });
  const [config, setConfig] = useState({ min_amount: 5000, currency: 'NGN' });
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Fetch config and rates
    Promise.all([
      axios.get(`${API_URL}/api/payments/config`),
      axios.get(`${API_URL}/api/rates`),
    ]).then(([configRes, ratesRes]) => {
      setConfig(configRes.data);
      const ratesMap = {};
      ratesRes.data.rates.forEach(rate => {
        ratesMap[rate.symbol] = rate;
      });
      setRates(ratesMap);
    }).catch(err => console.error('Failed to load config:', err));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const setQuickAmount = (amount) => {
    setFormData(prev => ({ ...prev, amount: amount.toString() }));
  };

  const calculateCrypto = () => {
    const rate = rates[formData.crypto_type];
    if (!rate || !formData.amount || !rate.buy_rate) return '0.00';
    return (parseFloat(formData.amount) / rate.buy_rate).toFixed(8);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const amount = parseFloat(formData.amount);
    if (amount < config.min_amount) {
      toast.error(`Minimum amount is ${formatPrice(config.min_amount)}`);
      return;
    }

    setLoading(true);
    try {
      const callback_url = `${window.location.origin}/payment/callback`;
      const response = await axios.post(`${API_URL}/api/payments/initialize`, {
        ...formData,
        amount: amount,
        callback_url,
      });

      // Redirect to Paystack payment page
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initialize payment');
      setLoading(false);
    }
  };

  const selectedCrypto = CRYPTO_OPTIONS.find(c => c.value === formData.crypto_type);
  const CryptoIcon = selectedCrypto?.icon || FaBitcoin;

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card-gold p-6 md:p-8"
      data-testid="payment-form"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-primary-main/10 flex items-center justify-center">
          <CreditCard size={24} className="text-primary-main" />
        </div>
        <div>
          <h3 className="font-heading text-2xl font-semibold text-text-primary">
            Buy Crypto with Card
          </h3>
          <p className="text-text-secondary text-sm">Pay instantly via Paystack</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-text-secondary text-sm mb-2">Full Name</label>
          <input
            type="text"
            name="full_name"
            value={formData.full_name}
            onChange={handleChange}
            required
            className="input-dark w-full"
            placeholder="Enter your full name"
            data-testid="pay-input-name"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-text-secondary text-sm mb-2">Email Address</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="input-dark w-full"
            placeholder="your@email.com"
            data-testid="pay-input-email"
          />
        </div>

        <div>
          <label className="block text-text-secondary text-sm mb-2">Cryptocurrency</label>
          <select
            name="crypto_type"
            value={formData.crypto_type}
            onChange={handleChange}
            className="input-dark w-full"
            data-testid="pay-select-crypto"
          >
            {CRYPTO_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-text-secondary text-sm mb-2">
            Amount in NGN (min ₦{config.min_amount?.toLocaleString()})
          </label>
          <input
            type="number"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            required
            min={config.min_amount}
            step="1000"
            className="input-dark w-full"
            placeholder="Enter amount"
            data-testid="pay-input-amount"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-text-secondary text-sm mb-2">Quick Select</label>
          <div className="flex flex-wrap gap-2">
            {QUICK_AMOUNTS.map(amount => (
              <button
                key={amount}
                type="button"
                onClick={() => setQuickAmount(amount)}
                className={`px-4 py-2 rounded-full text-sm transition-all ${
                  formData.amount === amount.toString()
                    ? 'bg-primary-main text-background font-semibold'
                    : 'bg-background border border-border text-text-secondary hover:border-primary-main/50'
                }`}
                data-testid={`quick-amount-${amount}`}
              >
                ₦{amount.toLocaleString()}
              </button>
            ))}
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-text-secondary text-sm mb-2">
            Your {formData.crypto_type} Wallet Address (to receive crypto)
          </label>
          <input
            type="text"
            name="wallet_address"
            value={formData.wallet_address}
            onChange={handleChange}
            required
            className="input-dark w-full font-mono text-sm"
            placeholder={`Enter your ${formData.crypto_type} wallet address`}
            data-testid="pay-input-wallet"
          />
        </div>
      </div>

      {/* Estimate */}
      {formData.amount && rates[formData.crypto_type] && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-6 p-4 bg-background rounded-lg border border-border"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CryptoIcon size={32} style={{ color: selectedCrypto.color }} />
              <div>
                <p className="text-text-secondary text-sm">You will receive approximately</p>
                <p className="font-heading text-xl font-bold gold-gradient-text">
                  {calculateCrypto()} {formData.crypto_type}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-text-secondary text-sm">Total to pay</p>
              <p className="font-heading text-xl font-bold text-text-primary">
                {formatPrice(parseFloat(formData.amount || 0))}
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Security Info */}
      <div className="mt-6 grid grid-cols-3 gap-2 text-xs text-text-muted">
        <div className="flex items-center gap-1 justify-center">
          <Shield size={14} className="text-accent-success" />
          <span>Secure</span>
        </div>
        <div className="flex items-center gap-1 justify-center">
          <Zap size={14} className="text-primary-main" />
          <span>Instant</span>
        </div>
        <div className="flex items-center gap-1 justify-center">
          <CreditCard size={14} className="text-secondary-main" />
          <span>Cards & Bank</span>
        </div>
      </div>

      {/* Submit */}
      <motion.button
        type="submit"
        disabled={loading}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="btn-gold w-full mt-6 flex items-center justify-center gap-2"
        data-testid="submit-payment-btn"
      >
        {loading ? (
          <span className="animate-pulse">Processing...</span>
        ) : (
          <>
            <CreditCard size={18} />
            Pay with Paystack
          </>
        )}
      </motion.button>

      <p className="text-text-muted text-xs mt-4 text-center flex items-center justify-center gap-1">
        <AlertCircle size={12} />
        Your crypto will be sent to your wallet after payment is confirmed
      </p>
    </motion.form>
  );
}
